import { FC, KeyboardEvent, ReactElement, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import {
	Box,
	Flex,
	HomeOutline,
	Modal,
	ModalContent,
	ModalOverlay,
	rem,
	SearchOutline,
	ShoppingCartOutline,
	Stack,
	Text,
	useBreakpointValue,
	useDisclosure,
} from '@bonusly/design-system';
import { useFeatureFlag } from '@bonusly/who-am-i';

import { useRewardsPanel } from '../../hooks/use-navbar-panels';
import { RewardsHighlightsPanel } from '../../modules/user-navbar/components/rewards-highlights-panel';
import { loadHighlights } from '../../queries/rewards';
import { SearchContent } from '../search';
import { CreateActionMenu } from './create-action-menu';
import { MoreActionMenu } from './more-action-menu';

const BOTTOM_NAV_HEIGHT = 84;
export const NAV_ITEM_WIDTH = 64;

// Same styling pattern as UserNavLink from user-navbar
export const getStyle = (isActive: boolean) => {
	if (isActive) {
		return {
			bg: 'primary.bgAlpha',
			color: 'primary.fgStrong',
		};
	}

	return {
		bg: 'transparent',
		color: 'secondary.fg',
	};
};

type MobileNavLinkProps = {
	to: string;
	icon: (color: string) => ReactElement;
	label: string;
};

const MobileNavLink: FC<MobileNavLinkProps> = ({ to, icon, label }) => {
	return (
		<Box as={NavLink} to={to} end w={rem(NAV_ITEM_WIDTH)} _focusVisible={{ outline: '2px solid', outlineColor: 'border.focus', outlineOffset: '-2px' }}>
			{({ isActive }: { isActive: boolean }) => {
				const styles = getStyle(isActive);
				return (
					<Stack gap={rem(4)} alignItems="center" justifyContent="center" borderRadius="md" py={rem(4)} px={rem(8)} {...styles}>
						{icon(styles.color)}
						<Text fontSize="xs" fontWeight="semibold" color={styles.color}>
							{label}
						</Text>
					</Stack>
				);
			}}
		</Box>
	);
};

type MobileNavButtonProps = {
	onClick: () => void;
	icon: ReactElement;
	label: string;
	ariaLabel?: string;
	buttonRef?: React.RefObject<HTMLButtonElement>;
};

const MobileNavButton: FC<MobileNavButtonProps> = ({ onClick, icon, label, ariaLabel, buttonRef }) => {
	const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			onClick();
		}
	};

	return (
		<Stack
			as="button"
			type="button"
			onClick={onClick}
			onKeyDown={handleKeyDown}
			aria-label={ariaLabel}
			ref={buttonRef}
			gap={rem(4)}
			alignItems="center"
			justifyContent="center"
			w={rem(NAV_ITEM_WIDTH)}
			cursor="pointer"
			color="secondary.fg"
			borderRadius="md"
			py={rem(4)}
			px={rem(8)}
			_hover={{ bg: 'secondary.bgAlpha' }}
			_focusVisible={{ outline: '2px solid', outlineColor: 'border.focus', outlineOffset: '-2px' }}
		>
			{icon}
			<Text fontSize="xs" fontWeight="semibold">
				{label}
			</Text>
		</Stack>
	);
};

export const GlobalMobileBottomNav: FC = () => {
	const { t } = useTranslation('navigation');
	const searchModalDisclosure = useDisclosure();
	const createMenuDisclosure = useDisclosure();
	const moreMenuDisclosure = useDisclosure();
	const searchButtonRef = useRef<HTMLButtonElement>(null);
	const isAtLeastSm = useBreakpointValue({ base: false, xs: true });
	const isRewardsMiniEnabled = useFeatureFlag('rewards-mini-page');
	const rewardsPanel = useRewardsPanel();

	const handleRewardsClick = async () => {
		if (rewardsPanel.isOpen) {
			rewardsPanel.close();
		} else {
			await loadHighlights();
			rewardsPanel.open();
		}
	};

	return (
		<>
			<Flex
				position="fixed"
				bottom={0}
				left={0}
				right={0}
				height={rem(BOTTOM_NAV_HEIGHT)}
				bg="bg.surface"
				borderTopWidth="1px"
				borderTopColor="border.default"
				alignItems="center"
				justifyContent="space-evenly"
				px={rem(8)}
				py={rem(16)}
				zIndex="docked"
			>
				{/* HOME */}
				<MobileNavLink to="/" icon={(color) => <HomeOutline size="xl" color={color} />} label={t('mobileBottomNav.home')} />

				{/* SEARCH */}
				<MobileNavButton
					onClick={searchModalDisclosure.onOpen}
					icon={<SearchOutline size="xl" />}
					label={t('mobileBottomNav.search')}
					ariaLabel={t('mobileBottomNav.search')}
					buttonRef={searchButtonRef}
				/>

				{/* CREATE */}
				<CreateActionMenu isOpen={createMenuDisclosure.isOpen} onOpen={createMenuDisclosure.onOpen} onClose={createMenuDisclosure.onClose} />

				{isRewardsMiniEnabled ? (
					<MobileNavButton
						onClick={handleRewardsClick}
						icon={<ShoppingCartOutline size="xl" color={rewardsPanel.isOpen ? 'primary.fgStrong' : 'secondary.fg'} />}
						label={t('mobileBottomNav.rewards')}
						ariaLabel={t('mobileBottomNav.rewards')}
					/>
				) : (
					<MobileNavLink to="/rewards" icon={(color) => <ShoppingCartOutline size="xl" color={color} />} label={t('mobileBottomNav.rewards')} />
				)}

				{/* MORE */}
				<MoreActionMenu isOpen={moreMenuDisclosure.isOpen} onOpen={moreMenuDisclosure.onOpen} onClose={moreMenuDisclosure.onClose} />
			</Flex>

			{/* SEARCH MODAL */}
			<Modal finalFocusRef={searchButtonRef} isOpen={searchModalDisclosure.isOpen} onClose={searchModalDisclosure.onClose} scrollBehavior="inside" size="2xl">
				<ModalOverlay />
				<ModalContent
					maxH={`min(calc(100% - ${rem(7.5)}), ${rem(664)})`}
					{...(!isAtLeastSm && {
						height: 'auto',
						maxHeight: '100%',
						borderRadius: 0,
						maxH: 'auto',
						my: 0,
					})}
				>
					<SearchContent onClose={searchModalDisclosure.onClose} />
				</ModalContent>
			</Modal>

			<RewardsHighlightsPanel isOpen={rewardsPanel.isOpen} onClose={rewardsPanel.close} isSidebarExpanded={false} />
		</>
	);
};

export const GLOBAL_MOBILE_BOTTOM_NAV_HEIGHT = BOTTOM_NAV_HEIGHT;
